<template>
	<div
		v-if="showState && !isLoading && (popupList?.length ?? 0) > 0 && !displayStore.isExtraSmallScreen"
		class="HomePopup"
	>
		<div class="dimmed" @click="isShow = false"></div>
		<div class="HomePopup_content">
			<div class="imageBox">
				<Swiper v-bind="homePopupSwiperOptions">
					<SwiperSlide v-for="item in popupList" :key="item.popup_pk" @click="moveToPopupUrl(item)">
						<img :src="item.popup_image" :alt="item.popup_name" loading="lazy" />
					</SwiperSlide>
				</Swiper>
			</div>
			<div class="buttonBox">
				<button @click="setCloseForToday">오늘 하루 안 보기</button>
				<button @click="isShow = false">닫기</button>
			</div>
		</div>
	</div>
	<BottomPopup
		v-if="showState && !isLoading && (popupList?.length ?? 0) > 0 && displayStore.isExtraSmallScreen"
		class="bottom_popup"
		@update:close="isShow = false"
	>
		<Swiper v-bind="homePopupSwiperOptions" class="swiper_list">
			<SwiperSlide v-for="item in popupList" :key="item.popup_pk" class="swiper_item" @click="moveToPopupUrl(item)">
				<img :src="item.popup_image" :alt="item.popup_name" loading="lazy" />
			</SwiperSlide>
		</Swiper>
	</BottomPopup>
</template>

<script setup lang="ts">
// core
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { Autoplay, Pagination } from 'swiper/modules';
// swiper
import { Swiper, SwiperSlide } from 'swiper/vue';

// api
import { GetPopupResType, useGetPopupApi } from '@/api/home';
import { useDisplayStore } from '@/stores/display';

import BottomPopup from './BottomPopup.vue';

// -----variable-----
const props = defineProps<{
	exposureLocation: string;
}>();

const route = useRoute();
const displayStore = useDisplayStore();
const isShow = ref(true);

// -----function-----
// 쿠키에 저장된 정보를 가져와서 반환하는 함수
const getCookie = (name: string) => {
	const cookieString = document.cookie;
	const cookies = cookieString.split(';').map((cookie) => cookie.trim());

	for (const cookie of cookies) {
		const [cookieName, cookieValue] = cookie.split('=');
		if (cookieName === name) {
			return decodeURIComponent(cookieValue);
		}
	}

	return null;
};
const setCloseForToday = () => {
	const expDate = new Date();
	expDate.setDate(expDate.getDate() + 1);
	expDate.setHours(9, 0, 0, 0);

	const jsonStringFromCookie = getCookie('location');
	const additionalString = String(route.name);
	let locationArr = jsonStringFromCookie?.split(',');
	if (!locationArr) {
		locationArr = [additionalString];
	} else if (!locationArr?.includes(additionalString)) {
		locationArr?.push(additionalString);
	}
	const finalString = locationArr?.join(',');

	document.cookie = 'closeForToday' + '=' + 'true' + '; expires=' + expDate.toUTCString();
	document.cookie = 'location' + '=' + `${finalString}` + '; expires=' + expDate.toUTCString();
	isShow.value = false;
};
const showState = computed(() => {
	if (!isShow.value) {
		return false;
	} else {
		const value = document.cookie.match('(^|;) ?' + 'closeForToday' + '=([^;]*)(;|$)');
		const location = getCookie('location')?.split(',');
		return !(value?.[2] === 'true' && location?.includes(String(route.name)));
	}
});

const { data: popupList, isLoading } = useGetPopupApi({
	enabled: showState,
	params: {
		exposure_location: props.exposureLocation,
	},
});

const moveToPopupUrl = (item: GetPopupResType) => {
	let agent = navigator.userAgent.toLowerCase();
	if (agent.indexOf('android') !== -1 && window.masterpick) {
		// window.masterpick.nativeUrlOpenBrowser(item.popup_mo_url);
		window.location.href = item.popup_url;
	} else if (
		(agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('macintosh') !== -1) &&
		window.webkit &&
		window.webkit.messageHandlers &&
		window.webkit.messageHandlers.nativeUrlOpenBrowser
	) {
		window.webkit.messageHandlers.nativeUrlOpenBrowser.postMessage(item.popup_mo_url);
	} else {
		window.open(item.popup_url, '_self');
	}
};

// 슬라이드 옵션들
const homePopupSwiperOptions = {
	autoplay: {
		delay: 5000,
		disableOnInteraction: false,
	},
	modules: [Pagination, Autoplay],
	pagination: true,
	loop: true,
};

// -----composition-----
watchEffect(() => {
	if (popupList.value && popupList.value?.length > 0 && showState.value) {
		document.body.style.overflow = 'hidden';
	} else {
		document.body.style.overflow = '';
	}
});
</script>

<style scoped lang="scss">
.HomePopup {
	z-index: 200;
	position: fixed;
	top: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	.dimmed {
		position: fixed;
		top: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.3);
	}
	.HomePopup_content {
		z-index: 300;
		width: calc(100% - 4rem);
		max-width: 42rem;
		height: auto;
		box-sizing: border-box;
		border-radius: var(--border-radius-large);
		overflow: hidden;
		box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.2);
		@media screen and (max-height: 600px) {
			max-width: 25rem;
			font-size: var(--font-s-mid);
			white-space: nowrap;
		}
		.imageBox {
			:deep(.swiper-slide) {
				aspect-ratio: 42/48;
				cursor: pointer;
				img {
					width: 100%;
					height: 48.8rem;
					object-fit: cover;
					object-position: center;
					background-color: var(--color-bg-light);
					overflow: hidden;
				}
			}
			:deep(.swiper-pagination) {
				bottom: 1.3rem;
				@media screen and (max-width: 768px) {
					bottom: 1rem;
				}
				.swiper-pagination-bullet {
					background-color: #7d7d8d;
					width: 1rem;
					height: 1rem;
					border-radius: var(--border-radius-full);
					transition: var(--transition-default);
					opacity: 1;
					&.swiper-pagination-bullet-active {
						background-color: #23242f;
					}
				}
			}
		}
		.buttonBox {
			display: flex;
			justify-content: center;
			align-items: center;
			color: var(--color-base-dark);
			font-weight: var(--font-w-semi-bold);
			font-size: var(--font-s-regular);
			line-height: 2.1rem;
			height: 5.2rem;
			button {
				flex: 1;
				padding: 2rem;
				&:first-child {
					background-color: #fff;
					color: var(--color-base-dark);
				}
				&:last-child {
					background-color: var(--color-primary);
					color: var(--color-base-white);
				}
				@media screen and (max-width: 768px) {
					font-size: var(--font-s-mid);
				}
			}
		}
	}
}
</style>
